import { FunctionComponent, useMemo, useState } from "react";

import {
  DefaultButton,
  Dialog,
  SubmissionForm,
  TextInputField
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Tenant, TenantDto } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useUpdateTenant } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { toTenantDto } from "@libs/api/gateways/plt/plt-gateway.utils";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

interface TenantEditDetailsFormDialogProps {
  tenant: Tenant;
}

interface TenantDetailsFormValues extends Pick<Tenant, "name"> {}

const validator = new Validator<TenantDetailsFormValues>(validator => ({
  name: validator
    .string()
    .required({ message: "Please fill out this required field" })
}));

const TenantEditDetailsFormDialogBase: FunctionComponent<TenantEditDetailsFormDialogProps & {
  onDismiss: () => void;
}> = ({ tenant, onDismiss }) => {
  const { mutateAsync: updateTenant } = useUpdateTenant();
  const defaultValues: TenantDetailsFormValues = useMemo(
    () => ({
      name: tenant.name
    }),
    [tenant]
  );

  const onSubmit = async (values: TenantDetailsFormValues) => {
    const payload: TenantDto = {
      ...toTenantDto(tenant),
      name: values.name
    };
    await updateTenant(payload);
    onDismiss();
  };

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "Update Tenant Details",
        showCloseButton: true,
        onDismiss
      }}
      minWidth={450}
    >
      <SubmissionForm<TenantDetailsFormValues>
        onSubmit={onSubmit}
        initialValues={defaultValues}
        validate={validator.validate}
        cancelButtonProps={{ onClick: onDismiss }}
      >
        {() => <TextInputField name="name" label="Name" required />}
      </SubmissionForm>
    </Dialog>
  );
};

export const TenantEditDetailsFormDialog: FunctionComponent<TenantEditDetailsFormDialogProps> = props => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogTenantWrite
  );
  return (
    <>
      <DefaultButton
        onClick={() => setShowDialog(true)}
        styles={{ root: { width: "fit-content" } }}
        disabled={!hasWritePermission}
        title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
      >
        Edit details
      </DefaultButton>

      {showDialog && (
        <TenantEditDetailsFormDialogBase
          {...props}
          onDismiss={() => setShowDialog(false)}
        />
      )}
    </>
  );
};
